import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          await refresh();
          setState("paid");
        } else if (attempts > 30) {
          clearInterval(iv);
          setState("failed");
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="surface p-10 max-w-md text-center">
        {state === "polling" && (
          <>
            <Loader2 className="animate-spin text-[hsl(var(--primary))] mx-auto" size={40} />
            <h1 className="font-display text-3xl mt-6">Confirming payment…</h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">This usually takes just a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="text-[hsl(var(--primary))] mx-auto" size={48} />
            <h1 className="font-display text-3xl mt-6">You're all set.</h1>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]">Your plan is now active. Keep writing — with more room than ever.</p>
            <Link to="/dashboard" data-testid="success-continue" className="btn-primary mt-8 inline-block">Go to dashboard</Link>
          </>
        )}
        {state === "failed" && (
          <>
            <h1 className="font-display text-3xl">Hmm, that took a while.</h1>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]">Your payment might still be processing. Check your dashboard or contact support.</p>
            <Link to="/dashboard" className="btn-primary mt-8 inline-block">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
